<template>
    <div id="account-search-view"
         class="full-vue-page">
        <div class="container container--top-padding-for-mobile-search">
            <div v-if="!isOnline"
                 class="alert alert-danger">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ l('MANAGE.OFFLINE_ALERT') }}
            </div>

            <div v-else
                 class="two-col-page">
                <div class="two-col-page__sidebar form-horizontal">
                    <AccountSearchForm ref="search-form"
                                       :has-results="results !== null"
                                       @clear="clearForm"
                                       @submit="onFormSubmit" />
                </div>

                <div class="two-col-page__content">
                    <PageLoadingSpinner v-if="showLoader"
                                        color="light"
                                        lang="MANAGE.SEARCHING" />
                    <ErrorMessage v-else-if="error"
                                  :error="error" />
                    <AccountSearchResults v-else-if="results && results.length > 0"
                                          :current-page="currentPage"
                                          :page-size="pageSize"
                                          :results="results" />
                    <div v-else-if="results">
                        <div class="alert alert-warning lead">
                            {{ l('MANAGE.NO_RESULTS') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="footer-btn-bar"
             :class="{visible: bottomBarVisible}">
            <div class="footer-btn-bar__left"></div>
            <div class="footer-btn-bar__right">
                <Pager v-model="currentPage"
                       :total-items="results ? results.length : 0"
                       :page-size="pageSize" />
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import AccountSearchForm from './components/AccountSearchForm';
import AccountSearchResults from './components/AccountSearchResults';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import EventNames from '@/lib/Events/EventNames';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import Pager from '@/vue/components/Pager';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    name: 'AccountSearchView',

    components: {
        AccountSearchForm,
        AccountSearchResults,
        ErrorMessage,
        PageLoadingSpinner,
        Pager,
    },

    data() {
        return {
            showLoader: false,

            error: null,

            isOnline: true,

            // Replaced with an actual function in 'created'.
            debouncedSearch: null,
        };
    },

    computed: {
        ...mapState('accountSearch', [
            'query',
            'results',
            'pageSize',
        ]),

        currentPage: {
            get() {
                return this.$store.state.accountSearch.currentPage;
            },
            set(page) {
                this.$store.commit('accountSearch/setCurrentPage', page);
            },
        },

        bottomBarVisible() {
            return !this.showLoader && !!this.results && (this.results.length > this.pageSize);
        },
    },

    watch: {
        bottomBarVisible: {
            handler(newValue) {
                this.$store.commit('setBottomBarVisible', newValue);
            },
            immediate: true,
        },

        currentPage() {
            this.$nextTick(() => {
                if (document.getElementById('account-search-view')) {
                    document.getElementById('account-search-view').scrollTop = 0;
                }
            });
        },
    },

    async created() {
        // Any time search is invoked, we want to wait a very short time in case it's called
        // again. This is because when a bar code is scanned, the keypresses are sent to the
        // browser, which invokes the search, then the bar code listener invokes the search.
        // The former may not have the right criteria in the form, so we want to suppress that
        // first search. We also don't want to run the same search multiple times!
        this.debouncedSearch = _.debounce(
            (query) => this.doSearch(query),
            1000,
            {
                leading: true,
                trailing: false,
            },
        );

        container.navigationService.showNav();

        // Early exit if no access permission
        await container.privilegesManager.requirePrivilegeOr403(PrivilegeNames.ACCESS_ACCOUNTS);

        this.isOnline = container.connectivityChecker.isOnline();
        this.deregisterOnlineStatusChangedListenerFunc = container.$rootScope.$on(
            EventNames.ONLINE_STATUS_CHANGED,
            () => {
                this.isOnline = container.connectivityChecker.isOnline();
            }
        );

        // Process the bar code if scanned.
        this.deregisterBarCodeScannedListenerFunc = container.$rootScope.$on(
            EventNames.BARCODE_SCANNED,
            (event, response) => {
                if (response.hasOpenModal) {
                    // Don't do anything on this page if a modal is open on top of it.
                    return;
                }
                this.onBarcodeScan(response.code);
            }
        );
    },

    mounted() {
        if (this.query) {
            // Load the cached query if there is one.
            this.$refs['search-form']?.setFormData(this.query);
        }
    },

    beforeUnmount() {
        this.$store.commit('setBottomBarVisible', false);

        this.debouncedSearch = null;
        if (this.deregisterOnlineStatusChangedListenerFunc) {
            this.deregisterOnlineStatusChangedListenerFunc();
        }
        if (this.deregisterBarCodeScannedListenerFunc) {
            this.deregisterBarCodeScannedListenerFunc();
        }
    },

    methods: {
        /**
         * @param {string} code
         */
        onBarcodeScan(code) {
            if (!code || !this.$refs['search-form']) {
                return;
            }

            // Put the scanned code in the form and submit it, so the same validation applies as
            // when the user searches manually.
            this.$refs['search-form'].setFormData(code);
            this.$refs['search-form'].submit();
        },

        clearForm() {
            this.$store.commit('accountSearch/clear');
            this.error = null;
            this.showLoader = false;
        },

        /**
         * @param {string} query
         */
        onFormSubmit(query) {
            if (typeof this.debouncedSearch === 'function') {
                this.debouncedSearch(query);
            }
        },

        /**
         * @param {string} query
         */
        doSearch(query) {
            this.$store.commit('accountSearch/clear');

            this.showLoader = true;
            this.error = null;

            container.customerAccountService.search({
                q: query,
                searchMemberships: 1,
                searchOrders: 1,
            })
                .then((results) => this.searchResultsLoaded(query, results))
                .catch((error) => {
                    this.error = error;
                    this.showLoader = false;
                    this.$refs['search-form']?.focusSearchInput();
                });
        },

        /**
         * @param {string} query
         * @param {CustomerAccount[]} results
         */
        searchResultsLoaded(query, results) {
            // Store the results in Vuex.
            // The results in the view come from Vuex, so setting this causes them to be displayed.
            this.$store.commit('accountSearch/setResults', { query, results });

            // Reset the current page when we search to prevent errors like page 7/5 after multiple searches.
            this.$store.commit('accountSearch/setCurrentPage', 1);

            this.showLoader = false;

            // If only 1 result, go straight to the details page.
            if (results.length === 1) {
                container.navigationService.viewAccountDetails(results[0].ID);
                return;
            }

            if (this.$refs['search-form']) {
                // If screen is small enough to be a mobile device we don't want to leave the focus on the input as
                // the keyboard takes up half the screen.
                // Focus search on desktop only.
                this.$refs['search-form'].focusSearchInput(false, true);
                // Blur search on mobile only.
                this.$refs['search-form'].blurSearchInput(true, false);
            }
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

@media (max-width: @mobile-max) {
    #account-search-view {
        // The search form goes into the header on mobile, so remove the margin on the now-empty sidebar.
        .two-col-page .two-col-page__sidebar {
            margin: 0;
        }
    }
}
</style>
